import { makeAutoObservable, observable } from 'mobx';

import { logger } from '@/lib/api/logger';
import { Socket } from '@/lib/api/Socket';

import { type TRootStore } from '../RootStoreMobx';

export class Search {
  constructor(root: TRootStore) {
    makeAutoObservable(this);
    this.root = root;
  }

  root: TRootStore;

  _isSearchModalDeprecatedOpen = false;
  hasAttemptedSearch = false;
  isSearching = false;
  searchTriggerLocation: USearchTriggerLocation = 'middle';
  searchState: USearchState = 'blur';
  searchInputRef: React.RefObject<HTMLInputElement> | null = null;

  searchResponse: SearchResponse = {
    events: observable([]),
    tournaments: observable([]),
  };

  setSearchTriggerLocation = (nextLocation: USearchTriggerLocation) => {
    this.searchTriggerLocation = nextLocation;
  };

  _setIsSearchModalDeprecatedOpen = (open = false) => {
    this._isSearchModalDeprecatedOpen = open;
  };

  setHasAttemptedSearch = (hasAttempted: boolean) => {
    this.hasAttemptedSearch = hasAttempted;
  };

  setIsSearching = (searching: boolean) => {
    this.isSearching = searching;
  };

  searchRequest = (query = '') => {
    const { disciplineIds } = this.root.ui;

    this.setIsSearching(true);
    this.setHasAttemptedSearch(true);

    try {
      Socket.send('rpc', 'search', { disciplines: disciplineIds, query });
    } catch (error) {
      if (error instanceof Error) {
        logger.error('📡 search: error sending search request', { error });
      }
      this.setIsSearching(false);
    }
  };

  setSearchResponse = (response: SearchResponse) => {
    this.searchResponse = response;
    this.setIsSearching(false);
  };

  resetSearchResult = () => {
    this.setSearchResponse({
      events: observable([]),
      tournaments: observable([]),
    });
  };

  get events() {
    return this.searchResponse.events ?? [];
  }

  get tournaments() {
    return this.searchResponse.tournaments ?? [];
  }

  get searchResult() {
    const result = {
      events: this.events.filter((el) => el.item?.type !== 1),
      outrights: this.events.filter((el) => el.item?.type === 1),
    };

    return result;
  }

  get isEmpty() {
    return (
      !this.searchResult.events.length &&
      !this.searchResult.outrights.length &&
      !this.tournaments.length
    );
  }

  get shouldShowEmptyMessage() {
    return this.hasAttemptedSearch && this.isEmpty && !this.isSearching;
  }

  openSearch = () => {
    if (
      this.searchTriggerLocation === 'left' &&
      !this.root.ui.isAnimalsRacingView &&
      !this.root.ui.isMobile &&
      !this.root.ui.isTablet &&
      !this.root.ui.isHideCategorizer
    ) {
      this._setIsSearchModalDeprecatedOpen(true);
    }

    this.searchState = 'focus';
    this._focusSearchInput();
  };

  private _focusAttempts = 0;
  private _focusAnimationFrame: number | null = null;
  private _focusSearchInput = () => {
    // this ensures an input will be focused no matter how search was opened:
    // - via actual click on the search button
    // - via query param
    // - via postMessage
    // - via legacy modal — when modal (left) version of search will be deleted, input focusing should become simpler

    // Clean up any existing animation frame
    if (this._focusAnimationFrame) {
      cancelAnimationFrame(this._focusAnimationFrame);
    }

    this._focusAnimationFrame = requestAnimationFrame(() => {
      this._focusAnimationFrame = null;

      if (this.searchInputRef?.current) {
        this.searchInputRef.current.focus();
        this._focusAttempts = 0; // Reset counter on success
      } else if (this._focusAttempts < 10) {
        // Limit retry attempts to prevent infinite recursion
        this._focusAttempts++;
        this._focusSearchInput();
      } else {
        console.warn('Search input ref not available after 10 attempts');
        this._focusAttempts = 0;
      }
    });
  };

  closeSearch = () => {
    this.searchState = 'blur';
    this.searchInputRef?.current?.blur();

    if (this._isSearchModalDeprecatedOpen) {
      this._setIsSearchModalDeprecatedOpen(false);
    }
  };

  setSearchInputRef = (ref: React.RefObject<HTMLInputElement> | null) => {
    this.searchInputRef = ref;
  };
}

/* Types */
type USearchTriggerLocation = 'left' | 'middle';
type USearchState = 'focus' | 'blur';

interface SearchResponse {
  events: { id: string; item: EN.Event }[];
  tournaments: { id: string; item: EN.Tournament }[];
}
